import { Button } from "@/components/ui/button";
import ClaimCard from "./ClaimCard";
import type { EditableClaim } from "@/lib/outline-import-types";

interface Props {
  conceptId: string;
  claims: EditableClaim[];
  onClaimsChange: (next: EditableClaim[]) => void;
  onCommit: () => void;
  onRepaste: () => void;
}

export default function PreviewPane({ conceptId, claims, onClaimsChange, onCommit, onRepaste }: Props) {
  const activeClaims = claims.filter((c) => !c.dropped);
  const activeEvidence = activeClaims.flatMap((c) => c.evidence.filter((e) => !e.skipped));
  const matched = activeEvidence.filter((e) => e.selectedPaperId !== null).length;
  const unmatched = activeEvidence.filter((e) => e.selectedPaperId === null).length;
  const hasUnresolved = unmatched > 0;
  const committableClaimCount = activeClaims.filter((c) => c.evidence.some((e) => !e.skipped)).length;

  function updateClaim(tmp_id: string, next: EditableClaim) {
    onClaimsChange(claims.map((c) => c.tmp_id === tmp_id ? next : c));
  }
  function dropClaim(tmp_id: string) {
    onClaimsChange(claims.map((c) => c.tmp_id === tmp_id ? { ...c, dropped: true } : c));
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-4 py-2 border-b text-sm flex items-center gap-3 bg-muted/40 shrink-0">
        <span>{activeClaims.length} claims</span>
        <span className="text-green-600 dark:text-green-400">· {matched} matched</span>
        <span className="text-amber-600 dark:text-amber-400">· {unmatched} unmatched</span>
        {hasUnresolved && (
          <span className="text-amber-600 dark:text-amber-400">
            — {unmatched} need attention
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {activeClaims.map((c) => (
          <ClaimCard
            key={c.tmp_id}
            claim={c}
            conceptId={conceptId}
            onUpdate={(n) => updateClaim(c.tmp_id, n)}
            onDrop={() => dropClaim(c.tmp_id)}
          />
        ))}
      </div>
      <div className="border-t p-3 flex justify-end gap-2 shrink-0 bg-background">
        <Button variant="ghost" onClick={() => window.history.back()}>Cancel</Button>
        <Button variant="outline" onClick={onRepaste}>Re-paste</Button>
        <Button onClick={onCommit} disabled={hasUnresolved}>
          Import {committableClaimCount}
        </Button>
      </div>
    </div>
  );
}
